import React from 'react';
import {
    FillsGraphConfiguration,
    FillsChartType,
    toggleConfigProperty,
} from 'widgets/fillsGraph/store/configuration/FillsGraphConfiguration';
import { useFillsGraphConfigurationSelector } from 'widgets/fillsGraph/store/configuration/selectors';
import { useDispatchUpdateFillsGraphConfiguration } from 'widgets/fillsGraph/store/configuration/dispatcher';
import { TooltipText } from 'utils/components/Tooltip/Tooltip';
import TimeSeriesLegend from 'utils/components/TimeSeriesChart/TimeSeriesLegend';
import classNames from 'classnames';
import { UseToggleConfigOptionRet } from 'widgets/store/configuration/configSlice';
import { WidgetLegend } from 'widgets/WidgetLegend/WidgetLegend';

export interface FillsChartToggleLegendProps<T extends FillsChartType> {
    alignDropdown?: 'left' | 'center' | 'right';
    className: string;
    label: string;
    value?: string;
    fillsGraphContainerLeft: number;
    suffix?: string;
    twoColourBorder?: boolean;
    hideDashWhenEmpty?: boolean;
    chart: T;
    property: keyof FillsGraphConfiguration[T];
    hideLegendProperty: keyof FillsGraphConfiguration[T];
    tooltip?: TooltipText;
}

export default function FillsChartToggleLegend<T extends FillsChartType>({
    alignDropdown,
    className,
    label,
    value,
    suffix,
    twoColourBorder,
    fillsGraphContainerLeft,
    hideDashWhenEmpty,
    chart,
    property,
    hideLegendProperty,
    tooltip,
}: FillsChartToggleLegendProps<T>) {
    const config = useFillsGraphConfigurationSelector();
    const updateConfig = useDispatchUpdateFillsGraphConfiguration();
    const toggleLegend = () =>
        updateConfig(toggleConfigProperty(config, chart, property));
    const legendDisabled = !config[chart][property];

    const toggleLegendVisible = () =>
        updateConfig(toggleConfigProperty(config, chart, hideLegendProperty));
    const legendHidden = !!config[chart][hideLegendProperty];

    const toggleLegendVisibleConfigOption: UseToggleConfigOptionRet = {
        value: legendHidden,
        toggle: toggleLegendVisible,
    };

    return (
        <WidgetLegend
            alignDropdown={alignDropdown}
            toggleLegendVisibleConfigOption={toggleLegendVisibleConfigOption}
            showSeries={!legendDisabled}
            toggleShowSeries={toggleLegend}
        >
            <TimeSeriesLegend
                className={classNames('FillsChartLegend', className)}
                label={label}
                value={value}
                suffix={suffix}
                twoColourBorder={twoColourBorder}
                containerLeft={fillsGraphContainerLeft}
                hideDashWhenEmpty={hideDashWhenEmpty}
                disabled={legendDisabled}
                onClick={toggleLegend}
                tooltip={tooltip}
            />
        </WidgetLegend>
    );
}
